import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

/**
 * MegaMenu — DeepMind-style dropdown panel for the centered nav.
 *
 * The wrapper holds the centering (so Framer Motion's transform animations
 * cannot clobber `-translate-x-1/2`). The motion.div only animates opacity,
 * y, and scale.
 */
export default function MegaMenu({ config, open, onClose, onMouseEnter, onMouseLeave }) {
  if (!config) return null;

  return (
    <AnimatePresence>
      {open && (
        <div
          data-testid="mega-menu"
          className="fixed left-1/2 -translate-x-1/2 z-40"
          style={{
            top: "calc(92px + env(safe-area-inset-top, 0px))",
            width: "min(980px, calc(100vw - 32px))",
          }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.99, transition: { duration: 0.18 } }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-lg overflow-hidden"
            style={{
              transformOrigin: "top center",
              background: "#141414",
              border: "1px solid rgba(255,255,255,0.08)",
              backdropFilter: "blur(28px)",
              WebkitBackdropFilter: "blur(28px)",
              boxShadow:
                "0 24px 64px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04)",
            }}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-6 px-8 md:px-10 pt-9 pb-7">
              <div>
                <h3
                  className="text-white"
                  style={{
                    fontFamily: "'Satoshi', sans-serif",
                    fontSize: 22,
                    fontWeight: 500,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {config.title}
                </h3>
                {config.subtitle && (
                  <p
                    className="mt-1 text-zinc-400"
                    style={{ fontSize: 14, fontWeight: 300 }}
                  >
                    {config.subtitle}
                  </p>
                )}
              </div>
              {config.cta && (
                <Link
                  to={config.cta.path}
                  onClick={onClose}
                  data-testid={`mega-cta-${config.cta.path.replace(/\//g, "-")}`}
                  className="hb-pill hb-pill-ghost whitespace-nowrap flex-shrink-0"
                  style={{ fontSize: 13 }}
                >
                  <span className="hb-pill-icon">
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="2"
                    >
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </span>
                  {config.cta.label}
                </Link>
              )}
            </div>

            {/* Hairline divider */}
            <div
              className="h-px mx-8 md:mx-10"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.20), transparent)",
              }}
            />

            {/* Columns */}
            <div
              className="grid gap-x-8 gap-y-10 px-8 md:px-10 pt-8 pb-10"
              style={{
                gridTemplateColumns: `repeat(${config.columns.length}, minmax(0, 1fr))`,
              }}
            >
              {config.columns.map((col, ci) => (
                <div key={col.heading + ci}>
                  <div
                    className="font-mono uppercase tracking-[0.22em] mb-5"
                    style={{ fontSize: 10, color: "#71717A" }}
                  >
                    {col.heading}
                  </div>
                  <ul className="space-y-2">
                    {col.items.map((it, ii) => (
                      <li key={it.label + ii}>
                        <Link
                          to={it.path}
                          onClick={onClose}
                          data-testid={`mega-item-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="group flex items-start gap-3.5 p-3 -mx-3 rounded-xl transition-all hover:bg-white/[0.04]"
                        >
                          {/* Icon tile. Minimal dark frame, cream stroke */}
                          <span
                            className="flex-shrink-0 mt-0.5 grid place-items-center transition-all group-hover:border-[#FAFAFA]/40"
                            style={{
                              width: 38,
                              height: 38,
                              borderRadius: 10,
                              background: "rgba(255,255,255,0.02)",
                              border: "1px solid rgba(255,255,255,0.10)",
                            }}
                          >
                            {it.icon}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="flex flex-wrap items-baseline gap-2">
                              {it.code && (
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                                  {it.code}
                                </span>
                              )}
                              <span
                                className="text-white group-hover:text-[#FAFAFA] transition-colors"
                                style={{
                                  fontFamily: "'Satoshi', sans-serif",
                                  fontSize: 15,
                                  fontWeight: 500,
                                }}
                              >
                                {it.label}
                              </span>
                            </span>
                            <span
                              className="block text-zinc-400 mt-0.5 leading-snug"
                              style={{ fontSize: 13, fontWeight: 300 }}
                            >
                              {it.desc}
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
